import React from 'react';
import { Moment } from 'moment';

import RecurrenceSettingsInput from 'Dashboard/components/RecurrenceSettingsInput/RecurrenceSettingsInput';
import RecurrenceTimeInMonthInput from 'Dashboard/components/RecurrenceTimeInMonthInput/RecurrenceTimeInMonthInput';
import DayPicker from 'Shared/components/DayPicker/DayPicker';
import TimeInput from 'Shared/components/TimeInput/TimeInput';
import CustomSingleDatePicker from 'Shared/components/CustomSingleDatePicker/CustomSingleDatePicker';
import { useTranslation } from 'Shared/contexts/TranslationsContext';

import RecurNextDate from './RecurNextDate';

import './RecurrenceEditor.scss';

type Props = {
  days: string;
  setDay: Function;
  intervalUnit: string;
  setIntervalUnit: Function;
  intervalAmount: string | number;
  setIntervalAmount: Function;
  timeOfDay: string;
  setTimeOfDay: Function;
  hideRecurrenceLabel?: boolean;
  startsAt: Moment;
  setStartsAt: Function;
  dayInMonth: number;
  setDayInMonth: Function;
  weekInMonth: number;
  setWeekInMonth: Function;
};

const formatRecurrenceInput = value => {
  return value ? parseInt(value, 10) : '';
};

const RecurrenceEditor = ({
  days,
  setDay,
  intervalUnit,
  setIntervalUnit,
  intervalAmount,
  setIntervalAmount,
  timeOfDay,
  setTimeOfDay,
  startsAt,
  setStartsAt,
  dayInMonth,
  setDayInMonth,
  weekInMonth,
  setWeekInMonth,
  hideRecurrenceLabel = false,
}: Props) => {
  const { tr } = useTranslation();
  const choosenDays = days?.split(' ') || [];

  const intervalUnitOptions = intervalAmount => [
    {
      value: 'day',
      label:
        intervalAmount > 1
          ? tr('perform.recurrence.interval.day_plural')
          : tr('perform.recurrence.interval.day_singular'),
    },
    {
      value: 'week',
      label:
        intervalAmount > 1
          ? tr('perform.recurrence.interval.week_plural')
          : tr('perform.recurrence.interval.week_singular'),
    },
    {
      value: 'month',
      label:
        intervalAmount > 1
          ? tr('perform.recurrence.interval.month_plural')
          : tr('perform.recurrence.interval.month_singular'),
    },
  ];

  const toggleDay = value => {
    let daysArray;
    if (choosenDays.includes(value)) {
      daysArray = choosenDays.length === 1 ? choosenDays : choosenDays.filter(choosenDay => choosenDay !== value);
    } else {
      daysArray = [...choosenDays, value];
    }
    setDay(daysArray.join(' '));
  };

  return (
    <div className="recurrence-editor">
      {!hideRecurrenceLabel && <label className="mt-3">{tr('perform.recurrence.editor.label_required')}</label>}
      <div className="d-flex align-items-center mb-2">
        <span className="start-date-label">{tr('perform.recurrence.editor.start_date')}</span>
        <CustomSingleDatePicker date={startsAt} onDateChange={setStartsAt} withPortal />
      </div>
      <RecurrenceSettingsInput
        label={tr('perform.recurrence.editor.repeat_every')}
        amountValue={intervalAmount}
        onAmountValueChange={e => setIntervalAmount(formatRecurrenceInput(e.target.value))}
        intervalValue={intervalUnit}
        intervalOptions={intervalUnitOptions(intervalAmount)}
        onIntervalValueChange={o => setIntervalUnit(o.value)}
      />

      {intervalUnit === 'week' ? (
        <div className="mt-2">
          {tr('perform.recurrence.editor.repeat_on')}
          <DayPicker choosenDays={choosenDays} onDayClick={toggleDay} />
        </div>
      ) : null}

      {intervalUnit === 'month' ? (
        <div className="mt-2">
          <RecurrenceTimeInMonthInput
            label={tr('perform.recurrence.editor.on')}
            startsAt={startsAt}
            dayInMonth={dayInMonth}
            weekInMonth={weekInMonth}
            onDayInMonthChange={setDayInMonth}
            onWeekInMonthChange={setWeekInMonth}
          />
        </div>
      ) : null}

      <div className="d-flex align-items-center my-2 mt-3">
        <div className="mr-2 time-of-day-label">{tr('perform.recurrence.editor.repeat_at')}</div>
        <TimeInput
          className="form-control time-of-day-input"
          value={timeOfDay}
          title={tr('perform.recurrence.editor.repeat_at')}
          required
          onChange={value => setTimeOfDay(value || '09:00')}
        />
      </div>
      <RecurNextDate
        days={days}
        timeOfDay={timeOfDay}
        intervalUnit={intervalUnit}
        intervalAmount={intervalAmount}
        startsAt={startsAt}
        dayInMonth={dayInMonth}
        weekInMonth={weekInMonth}
      />
    </div>
  );
};

export default RecurrenceEditor;
